/**
 * RestorationTinTileRepairs v2 — Stage-1 Tin Tile builder (Decramastic / Colortile).
 *
 * Sections (Feb '26 v2 spec):
 *   1. Bottom course screws — 50/65mm timbertek dropdown, 4 screws/m
 *   2. Ridge — cap selector + length_mm + grub screw + per-cap/per-m labour
 *   3. Barge — same shape as Ridge
 *   4. Panel beat — per-tile vs hourly toggle + comparison panel
 *   5. Replace damaged tiles — profile/brand-filtered rates + mode toggle
 *   6. Rust treatment — 4 preset locations + per-step (wire-brush / kill / prime) editor
 *   7. Pipe/Flue Flashings — Btray + Dektite combo (unchanged)
 *
 * Stage-2 Paint reuses Stage2PaintSection from RestorationConcreteRepairs.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, Wrench, Pipette, Triangle, Hammer, Flame, Clock, Calculator, Edit3, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Stage2PaintSection } from '@/components/RestorationConcreteRepairs';
import EditRateLink from '@/components/EditRateLink';
import IronRateDetailModal from '@/components/IronRateDetailModal';
import ComponentsFlashingEmbed from '@/components/shared/ComponentRow/ComponentsFlashingEmbed';

const TIN_TILE_SUBTYPES = new Set([
  'decramastic', 'decra', 'chipped tin tile', 'chipped tin', 'chipped',
  'colortile', 'color tile', 'colourtile', 'colour tile',
  'tin tile', 'tin',
]);
export function isTinTileSubtype(s) {
  return TIN_TILE_SUBTYPES.has(((s || '').toString().toLowerCase().trim()));
}


/**
 * Iter 103 — `EditRateLink` extracted to `@/components/EditRateLink`
 * so the Concrete, Iron, and Tin Tile builders all share one
 * implementation.
 */


export default function RestorationTinTileRepairs({ api, appraisalId, subtype, onLinesChanged, onJumpToPin, onOpenAnnotator, paintHiddenByDefault = false }) {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState({ lines: [], total: 0, line_count: 0, stage_totals: { stage_1: 0, stage_2: 0 }, stage_counts: { stage_1: 0, stage_2: 0 } });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [rustEditOpen, setRustEditOpen] = useState(false);
  // Iter 121 — IronRateDetailModal state hoisted at the parent so the
  // overlay renders at the top level and avoids z-index clashes with
  // the nested <details> sections.
  const [detailModal, setDetailModal] = useState({ open: false, component: 'tile', rate: null, pinContext: null });
  // v10.20 — Repairs-context render collapses Stage-2 Paint by default
  // (mirrors Iron/Concrete). Operator opts in via a reveal button.
  const [paintRevealed, setPaintRevealed] = useState(!paintHiddenByDefault);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/restoration/appraisals/${appraisalId}/tin-tile-repairs/config`);
      const { data: p } = await api.get(`/restoration/appraisals/${appraisalId}/tin-tile-repairs/preview`);
      setData(d); setPreview(p);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  }, [api, appraisalId]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (cfg) => {
    setBusy(true); setErr(null);
    try {
      await api.put(`/restoration/appraisals/${appraisalId}/tin-tile-repairs/config`, cfg);
      const { data: p } = await api.get(`/restoration/appraisals/${appraisalId}/tin-tile-repairs/preview`);
      setPreview(p);
      if (onLinesChanged) await onLinesChanged();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, [api, appraisalId, onLinesChanged]);

  const update = (sectionPatch) => {
    const next = { ...data.config, ...sectionPatch };
    setData(d => ({ ...d, config: next }));
    save(next);
  };

  // Iter 121 — Open IronRateDetailModal for a Tin Tile section's rate
  // row. `pinContext.label` shows in the modal header; `onSaveOverride`
  // receives the override draft and writes it onto the matching
  // section's `*_detail_override` block via `update`.
  const openRateDetail = useCallback((component, rate, pinContext) => {
    if (!rate || !rate.id) return;
    setDetailModal({ open: true, component, rate, pinContext: pinContext || null });
  }, []);
  const closeRateDetail = useCallback(() => {
    setDetailModal(prev => ({ ...prev, open: false }));
  }, []);

  if (!isTinTileSubtype(subtype)) {
    return <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs text-amber-800" data-testid="tin-tile-not-applicable">
      <AlertCircle size={12} className="inline mr-1" /> Tin Tile Repairs builder applies to Decramastic / Colortile roofs. Current subtype: <strong>{subtype || '(none)'}</strong>.
    </div>;
  }
  if (!data) return <div className="bg-white border border-slate-200 rounded-md p-4 text-xs text-slate-500" data-testid="tin-tile-loading"><Loader2 size={11} className="inline animate-spin mr-1" /> Loading Tin Tile Repairs…</div>;
  if (err) return <div className="bg-rose-50 border border-rose-300 rounded-md p-3 text-xs text-rose-700" data-testid="tin-tile-error"><AlertCircle size={12} className="inline mr-1" /> {err}</div>;

  const cfg = data.config;
  return (
    <div className="space-y-3" data-testid="tin-tile-repairs-section">
      <div className="bg-white border border-slate-200 rounded-md px-4 py-2.5 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><Wrench size={14} className="text-amber-700" /> Tin Tile Repairs — {subtype}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Stage 1: Screws · Ridges · Barges · Panel beat · Replace tiles · Rust · Pipe/Flue. Stage 2: Paint via {subtype.toLowerCase().includes('color') ? 'Colortile 4-coat' : 'Decramastic 6-coat (chip)'}.</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Tin sub-total</div>
          <div className="text-lg font-bold text-amber-800 font-mono-num" data-testid="tin-tile-stage1-total">${preview.stage_totals.stage_1.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500">{preview.stage_counts?.stage_1 || 0} line{preview.stage_counts?.stage_1 === 1 ? '' : 's'}</div>
        </div>
      </div>


      <BottomCourseSection cfg={cfg.screws_bottom} dims={data.dims} options={data.options}
                            busy={busy} onChange={(next) => update({ screws_bottom: next })}
                            onOpenRateDetail={openRateDetail} />

      <CapSection title="Ridge — caps + per-metre rescrew" testidBase="ridge"
                   cfg={cfg.ridge} dims={data.dims}
                   defaultLengthM={data.dims.ridge_total_m}
                   capOptions={data.options.ridge_caps}
                   perMOptions={data.options.ridge_screws_per_m}
                   capComponent="ridge"
                   busy={busy} onChange={(next) => update({ ridge: next })}
                   onOpenRateDetail={openRateDetail} />

      <CapSection title="Barge — caps + per-metre rescrew" testidBase="barge"
                   cfg={cfg.barge} dims={data.dims}
                   defaultLengthM={data.dims.barge_total_m}
                   capOptions={data.options.barge_caps}
                   perMOptions={data.options.barge_screws_per_m}
                   capComponent="barge"
                   busy={busy} onChange={(next) => update({ barge: next })}
                   onOpenRateDetail={openRateDetail} />

      <PanelBeatSection cfg={cfg.panel_beat} defaultQty={data.defect_qty}
                         busy={busy} onChange={(next) => update({ panel_beat: next })} />

      <ReplaceTileSection cfg={cfg.replace_tile} defaultQty={data.defect_qty}
                           options={data.options.replace_tile}
                           profile={data.options.profile} brand={data.options.brand}
                           busy={busy} onChange={(next) => update({ replace_tile: next })}
                           onOpenRateDetail={openRateDetail} />

      <RustTreatmentSection cfg={cfg.rust_treatment}
                              dims={data.dims} roof_m2={data.roof_m2}
                              busy={busy}
                              onChange={(next) => update({ rust_treatment: next })}
                              onEditSteps={() => setRustEditOpen(true)} />

      <RustStepsDialog open={rustEditOpen} onOpenChange={setRustEditOpen}
                        steps={cfg.rust_treatment.steps}
                        onSave={(steps) => { update({ rust_treatment: { ...cfg.rust_treatment, steps } }); setRustEditOpen(false); }} />

      <FlashingSection items={cfg.flashing.items} api={api} appraisalId={appraisalId} />

      {/* v10.20 — Stage-2 Paint hidden behind a reveal in Repairs-only
          contexts to stop the Paint Systems form bleeding into pure
          Repairs quotes. */}
      {paintHiddenByDefault && !paintRevealed ? (
        <button
          type="button"
          data-testid="tin-tile-repairs-reveal-paint"
          onClick={() => setPaintRevealed(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50 text-emerald-800 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
          title="Open the Stage 2 — Paint Systems picker for this Repairs job"
        >
          🎨 + Select painting info
          <span className="text-[11px] font-normal text-emerald-700/70 ml-1">(Stage 2 paint hidden by default for Repairs)</span>
        </button>
      ) : (
        <Stage2PaintSection api={api} appraisalId={appraisalId} onLinesChanged={load} />
      )}

      <CalculatedLinesPanel preview={preview} />

      <IronRateDetailModal
        api={api}
        open={detailModal.open}
        onOpenChange={(v) => v ? null : closeRateDetail()}
        component={detailModal.component}
        rate={detailModal.rate}
        pinContext={detailModal.pinContext}
        onSave={() => load()}
      />
    </div>
  );
}


// ── Bottom course screws (50/65mm) ────────────────────────────────────


function BottomCourseSection({ cfg, dims, options, busy, onChange, onOpenRateDetail }) {
  const optionList = (cfg.screw_type === '65mm' ? options.screws_bottom_65mm : options.screws_bottom_50mm) || [];
  const rate = optionList.find(o => o.id === cfg.rate_id) || optionList[0];
  const inherit = cfg.length_m_override == null;
  const effLength = inherit ? Number(dims.perimeter_m || 0) : Number(cfg.length_m_override || 0);
  const ov = cfg.detail_override || {};
  const hasOverride = Object.keys(ov).length > 0;
  const effRate = rate ? (
    hasOverride
      ? Math.round(((Number(ov.materials_rate ?? rate.material) || 0) + (Number(ov.labour_rate ?? rate.labour) || 0)) * 100) / 100
      : rate.rate
  ) : 0;
  const total = (cfg.enabled && rate) ? effLength * effRate : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="screws-bottom-section">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm font-bold text-slate-800 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} data-testid="screws-bottom-enabled"
                 onChange={(e) => onChange({ ...cfg, enabled: e.target.checked, rate_id: cfg.rate_id || (optionList[0]?.id || '') })}
                 className="rounded text-amber-600 focus:ring-amber-400" />
          Bottom course screws (perimeter)
        </label>
        <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div><div className="text-base font-bold text-amber-800 font-mono-num" data-testid="screws-bottom-total">${total.toFixed(2)}</div></div>
      </div>
      {cfg.enabled && (
        <div className="p-4 grid grid-cols-12 gap-3 text-xs">
          <label className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Screw type</span>
            <select value={cfg.screw_type || '50mm'} disabled={busy}
                    data-testid="screws-bottom-type"
                    onChange={(e) => onChange({ ...cfg, screw_type: e.target.value, rate_id: '' })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              <option value="50mm">50 mm TimberTek (12*50)</option>
              <option value="65mm">65 mm TimberTek (12*65)</option>
            </select>
          </label>
          <label className="col-span-6 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
              <span>Fastening rate (per m)</span>
              <span className="flex items-center gap-1.5">
                {rate && onOpenRateDetail && (
                  <button type="button" data-testid="screws-bottom-detail"
                          onClick={() => onOpenRateDetail('fastening', rate, {
                            label: 'Bottom course',
                            detailOverride: ov,
                            onSaveOverride: async (body) => onChange({ ...cfg, detail_override: body }),
                          })}
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${hasOverride ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                    {hasOverride ? '✎ Override' : '✎ Detail'}
                  </button>
                )}
                {rate && <EditRateLink component="fastening" rateId={rate.id} testid="screws-bottom-edit" />}
              </span>
            </span>
            <select value={cfg.rate_id || (optionList[0]?.id || '')} disabled={busy}
                    data-testid="screws-bottom-rate"
                    onChange={(e) => onChange({ ...cfg, rate_id: e.target.value })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              {optionList.length === 0 && <option value="">No "Rescrew ColourTile Bottom Course" rate row in catalogue</option>}
              {optionList.map(o => <option key={o.id} value={o.id}>{o.name} · ${o.rate.toFixed(2)}/m</option>)}
            </select>
          </label>
          <label className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Perimeter (m){inherit && <span className="text-emerald-600 normal-case font-normal"> · auto</span>}</span>
            <div className="flex items-center gap-1">
              <input type="number" step="0.1" min="0" value={cfg.length_m_override ?? ''}
                     placeholder={dims.perimeter_m > 0 ? `auto: ${dims.perimeter_m.toFixed(1)}` : '0'}
                     data-testid="screws-bottom-length-override"
                     onChange={(e) => onChange({ ...cfg, length_m_override: e.target.value === '' ? null : parseFloat(e.target.value) })}
                     className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
              {cfg.length_m_override != null && (
                <button type="button" data-testid="screws-bottom-length-reset"
                        onClick={() => onChange({ ...cfg, length_m_override: null })}
                        className="text-[10px] text-slate-500 hover:text-amber-700 underline">reset</button>
              )}
            </div>
          </label>
          <div className="col-span-12 text-xs text-slate-600">
            <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {rate ? <>{effLength.toFixed(1)} m × <strong>${effRate.toFixed(2)}/m</strong> = <strong className="text-amber-800">${total.toFixed(2)}</strong>{hasOverride && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 font-bold">· override</span>}</> : <span className="text-slate-400 italic">pick a "Rescrew ColourTile Bottom Course" rate row</span>}
              <span className="text-slate-500 italic ml-2">(per-metre rate — screw count already baked in)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Ridge / Barge cap section (cap + grub screws + per-cap/per-m labour) ──


function CapSection({ title, testidBase, cfg, dims, defaultLengthM, capOptions, perMOptions, capComponent, busy, onChange, onOpenRateDetail }) {
  const capRow = capOptions.find(o => o.id === cfg.cap_rate_id);
  const perMRow = perMOptions.find(o => o.id === cfg.per_m_rate_id);
  const inheritLen = cfg.length_m_override == null;
  const ridgeLength = inheritLen ? Number(defaultLengthM || 0) : Number(cfg.length_m_override || 0);
  const inheritCapMm = cfg.cap_length_mm_override == null;
  const capLengthMm = inheritCapMm ? Number(capRow?.length_mm || 1200) : Number(cfg.cap_length_mm_override || 0);
  const capLengthM = capLengthMm / 1000;
  const capQty = capLengthM > 0 ? Math.ceil(ridgeLength / capLengthM) : 0;
  const capOv = cfg.cap_detail_override || {};
  const pmOv = cfg.per_m_detail_override || {};
  const hasCapOverride = Object.keys(capOv).length > 0;
  const hasPmOverride = Object.keys(pmOv).length > 0;
  const effCapRate = capRow ? (
    hasCapOverride
      ? Math.round(((Number(capOv.materials_rate ?? capRow.material) || 0) + (Number(capOv.labour_rate ?? capRow.labour) || 0)) * 100) / 100
      : capRow.rate
  ) : 0;
  const effPmRate = perMRow ? (
    hasPmOverride
      ? Math.round(((Number(pmOv.materials_rate ?? perMRow.material) || 0) + (Number(pmOv.labour_rate ?? perMRow.labour) || 0)) * 100) / 100
      : perMRow.rate
  ) : 0;
  const capTotal = capRow ? capQty * effCapRate : 0;
  const perMTotal = perMRow ? ridgeLength * effPmRate : 0;
  const total = (cfg.enabled ? (capTotal + perMTotal) : 0);
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={`${testidBase}-section`}>
      <div className="px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm font-bold text-slate-800 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} data-testid={`${testidBase}-enabled`}
                 onChange={(e) => onChange({ ...cfg, enabled: e.target.checked,
                                              cap_rate_id: cfg.cap_rate_id || (capOptions[0]?.id || ''),
                                              per_m_rate_id: cfg.per_m_rate_id || (perMOptions[0]?.id || '') })}
                 className="rounded text-amber-600 focus:ring-amber-400" />
          {title}
        </label>
        <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div><div className="text-base font-bold text-amber-800 font-mono-num" data-testid={`${testidBase}-total`}>${total.toFixed(2)}</div></div>
      </div>
      {cfg.enabled && (
        <div className="p-4 grid grid-cols-12 gap-3 text-xs">
          <label className="col-span-6 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
              <span>Cap type (supply)</span>
              <span className="flex items-center gap-1.5">
                {capRow && onOpenRateDetail && (
                  <button type="button" data-testid={`${testidBase}-cap-detail`}
                          onClick={() => onOpenRateDetail(capComponent || testidBase, capRow, {
                            label: `${testidBase} cap`,
                            detailOverride: capOv,
                            onSaveOverride: async (body) => onChange({ ...cfg, cap_detail_override: body }),
                          })}
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${hasCapOverride ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                    {hasCapOverride ? '✎ Override' : '✎ Detail'}
                  </button>
                )}
                {capRow && <EditRateLink component={capComponent || testidBase} rateId={capRow.id} testid={`${testidBase}-cap-edit`} />}
              </span>
            </span>
            <select value={cfg.cap_rate_id || ''} disabled={busy}
                    data-testid={`${testidBase}-cap-rate`}
                    onChange={(e) => onChange({ ...cfg, cap_rate_id: e.target.value, cap_length_mm_override: null })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              {capOptions.length === 0 && <option value="">No cap rate rows</option>}
              {capOptions.map(o => <option key={o.id} value={o.id}>{o.name} · ${o.rate.toFixed(2)} · {o.length_mm > 0 ? `${o.length_mm} mm` : 'no length set'}</option>)}
            </select>
          </label>
          <label className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cap length (mm){inheritCapMm && capRow?.length_mm ? <span className="text-emerald-600 normal-case font-normal"> · catalog</span> : ''}</span>
            <div className="flex items-center gap-1">
              <input type="number" step="50" min="0"
                     value={cfg.cap_length_mm_override ?? ''}
                     placeholder={capRow?.length_mm ? `catalog: ${capRow.length_mm}` : '1200'}
                     data-testid={`${testidBase}-cap-length-override`}
                     onChange={(e) => onChange({ ...cfg, cap_length_mm_override: e.target.value === '' ? null : parseFloat(e.target.value) })}
                     className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
              {cfg.cap_length_mm_override != null && (
                <button type="button" data-testid={`${testidBase}-cap-length-reset`}
                        onClick={() => onChange({ ...cfg, cap_length_mm_override: null })}
                        className="text-[10px] text-slate-500 hover:text-amber-700 underline">reset</button>
              )}
            </div>
          </label>
          <label className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Length (m){inheritLen && <span className="text-emerald-600 normal-case font-normal"> · auto</span>}</span>
            <input type="number" step="0.1" min="0"
                   value={cfg.length_m_override ?? ''}
                   placeholder={defaultLengthM > 0 ? `auto: ${Number(defaultLengthM).toFixed(1)}` : '0'}
                   data-testid={`${testidBase}-length-override`}
                   onChange={(e) => onChange({ ...cfg, length_m_override: e.target.value === '' ? null : parseFloat(e.target.value) })}
                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
          </label>
          <label className="col-span-12 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
              <span>Per-metre rescrew rate (combined supply + labour)</span>
              <span className="flex items-center gap-1.5">
                {perMRow && onOpenRateDetail && (
                  <button type="button" data-testid={`${testidBase}-per-m-detail`}
                          onClick={() => onOpenRateDetail('fastening', perMRow, {
                            label: `${testidBase} rescrew`,
                            detailOverride: pmOv,
                            onSaveOverride: async (body) => onChange({ ...cfg, per_m_detail_override: body }),
                          })}
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${hasPmOverride ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                    {hasPmOverride ? '✎ Override' : '✎ Detail'}
                  </button>
                )}
                {perMRow && <EditRateLink component="fastening" rateId={perMRow.id} testid={`${testidBase}-per-m-edit`} />}
              </span>
            </span>
            <select value={cfg.per_m_rate_id || ''} disabled={busy}
                    data-testid={`${testidBase}-per-m-rate`}
                    onChange={(e) => onChange({ ...cfg, per_m_rate_id: e.target.value })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              <option value="">— skip per-metre rescrew —</option>
              {perMOptions.map(o => <option key={o.id} value={o.id}>{o.name} · ${o.rate.toFixed(2)}/m</option>)}
            </select>
          </label>
          <div className="col-span-12 text-xs text-slate-600">
            <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1 space-y-0.5">
              {capRow && <>
                Caps: <strong>{ridgeLength.toFixed(2)} m</strong> ÷ <strong>{capLengthMm} mm</strong> = <strong>{capQty} caps</strong> × <strong>${effCapRate.toFixed(2)}</strong> = <strong>${capTotal.toFixed(2)}</strong>{hasCapOverride && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 font-bold">· override</span>}
              </>}
              {!capRow && <span className="text-slate-400 italic">pick a cap type</span>}
              {perMRow && <div>
                Rescrew: <strong>{ridgeLength.toFixed(2)} m</strong> × <strong>${effPmRate.toFixed(2)}/m</strong> = <strong>${perMTotal.toFixed(2)}</strong>{hasPmOverride && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 font-bold">· override</span>}
              </div>}
              <div className="border-t border-amber-300 pt-0.5 font-semibold text-amber-800">Total = ${total.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Panel Beat — per-tile vs hourly mode + comparison panel ──────────


function PanelBeatSection({ cfg, defaultQty, busy, onChange }) {
  const inherit = cfg.qty_override == null || cfg.qty_override === '';
  const qty = inherit ? Number(defaultQty || 0) : Number(cfg.qty_override || 0);
  const rpt = Number(cfg.rate_per_tile || 0);
  const hourly = Number(cfg.hourly_rate || 75);
  const mins = Number(cfg.minutes_per_tile || 5);
  const perTileTotal = qty * rpt;
  const hours = qty * mins / 60;
  const hourlyTotal = hours * hourly;
  const mode = cfg.mode || 'per_tile';
  const total = (cfg.enabled && qty > 0) ? (mode === 'hourly' ? hourlyTotal : perTileTotal) : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="panel-beat-section">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm font-bold text-slate-800 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} data-testid="panel-beat-enabled"
                 onChange={(e) => onChange({ ...cfg, enabled: e.target.checked })}
                 className="rounded text-amber-600 focus:ring-amber-400" />
          <Hammer size={13} className="text-amber-700" /> Panel beat dented tiles
        </label>
        <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total ({mode === 'hourly' ? 'hourly' : 'per tile'})</div><div className="text-base font-bold text-amber-800 font-mono-num" data-testid="panel-beat-total">${total.toFixed(2)}</div></div>
      </div>
      {cfg.enabled && (
        <div className="p-4 grid grid-cols-12 gap-3 text-xs">
          <label className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Qty {inherit && <span className="text-emerald-600 normal-case font-normal">· auto</span>}</span>
            <input type="number" step="1" min="0" value={cfg.qty_override ?? ''} placeholder={defaultQty > 0 ? `auto: ${defaultQty}` : '0'}
                   data-testid="panel-beat-qty-override"
                   onChange={(e) => onChange({ ...cfg, qty_override: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
          </label>
          <div className="col-span-9 inline-flex bg-slate-100 rounded p-0.5 self-end h-7" role="tablist" data-testid="panel-beat-mode">
            {[
              { v: 'per_tile', label: '$/tile' },
              { v: 'hourly', label: 'Hourly' },
            ].map(o => (
              <button key={o.v} type="button" disabled={busy}
                      data-testid={`panel-beat-mode-${o.v}`}
                      onClick={() => onChange({ ...cfg, mode: o.v })}
                      className={`px-3 py-1 text-xs font-semibold rounded transition-all ${mode === o.v ? 'bg-amber-700 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>{o.label}</button>
            ))}
          </div>
          <div className={`col-span-6 p-2 rounded ${mode === 'per_tile' ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-slate-200'}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Per-tile rate</div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] text-slate-500">$ per tile</span>
              <input type="number" step="0.5" min="0" value={cfg.rate_per_tile ?? 0}
                     data-testid="panel-beat-rate-per-tile"
                     onChange={(e) => onChange({ ...cfg, rate_per_tile: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                     className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
            </label>
            <div className="text-[11px] mt-1.5">{qty} × ${rpt.toFixed(2)} = <strong>${perTileTotal.toFixed(2)}</strong></div>
          </div>
          <div className={`col-span-6 p-2 rounded ${mode === 'hourly' ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-slate-200'}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Hourly</div>
            <div className="grid grid-cols-2 gap-1">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-slate-500">Min/tile</span>
                <input type="number" step="0.5" min="0" value={cfg.minutes_per_tile ?? 5}
                       data-testid="panel-beat-minutes-per-tile"
                       onChange={(e) => onChange({ ...cfg, minutes_per_tile: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                       className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-slate-500">$ / hr</span>
                <input type="number" step="5" min="0" value={cfg.hourly_rate ?? 75}
                       data-testid="panel-beat-hourly-rate"
                       onChange={(e) => onChange({ ...cfg, hourly_rate: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                       className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
              </label>
            </div>
            <div className="text-[11px] mt-1.5">{qty} tiles × {mins}min = <strong>{hours.toFixed(2)} hr</strong> × ${hourly} = <strong>${hourlyTotal.toFixed(2)}</strong></div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Replace damaged tiles ────────────────────────────────────────────


function ReplaceTileSection({ cfg, defaultQty, options, profile, brand, busy, onChange, onOpenRateDetail }) {
  const rate = options.find(o => o.id === cfg.rate_id) || options[0];
  const inherit = cfg.qty_override == null || cfg.qty_override === '';
  const qty = inherit ? Number(defaultQty || 0) : Number(cfg.qty_override || 0);
  const ov = cfg.detail_override || {};
  const hasOverride = Object.keys(ov).length > 0;
  const effRate = rate ? (
    hasOverride
      ? Math.round(((Number(ov.materials_rate ?? rate.material) || 0) + (Number(ov.labour_rate ?? rate.labour) || 0)) * 100) / 100
      : rate.rate
  ) : 0;
  const supplyTotal = qty * effRate;
  const mode = cfg.mode || 'per_tile';
  const lpt = Number(cfg.labour_per_tile || 0);
  const perTileLab = qty * lpt;
  const mins = Number(cfg.minutes_per_tile || 8);
  const hours = qty * mins / 60;
  const hourly = Number(cfg.hourly_rate || 75);
  const hourlyLab = hours * hourly;
  const labTotal = mode === 'hourly' ? hourlyLab : perTileLab;
  const total = cfg.enabled && qty > 0 ? supplyTotal + labTotal : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="replace-tile-section">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm font-bold text-slate-800 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} data-testid="replace-tile-enabled"
                 onChange={(e) => onChange({ ...cfg, enabled: e.target.checked, rate_id: cfg.rate_id || (options[0]?.id || '') })}
                 className="rounded text-amber-600 focus:ring-amber-400" />
          <Triangle size={13} className="text-amber-700" /> Replace damaged tin tiles
        </label>
        <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div><div className="text-base font-bold text-amber-800 font-mono-num" data-testid="replace-tile-total">${total.toFixed(2)}</div></div>
      </div>
      {cfg.enabled && (
        <div className="p-4 grid grid-cols-12 gap-3 text-xs">
          <label className="col-span-6 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
              <span>Rate row {profile && <span className="text-emerald-600 normal-case font-normal">· profile {profile}{brand ? ` · ${brand}` : ''}</span>}</span>
              <span className="flex items-center gap-1.5">
                {rate && onOpenRateDetail && (
                  <button type="button" data-testid="replace-tile-detail"
                          onClick={() => onOpenRateDetail('tile', rate, {
                            label: 'Replace tile',
                            detailOverride: ov,
                            onSaveOverride: async (body) => onChange({ ...cfg, detail_override: body }),
                          })}
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${hasOverride ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                    {hasOverride ? '✎ Override' : '✎ Detail'}
                  </button>
                )}
                {rate && <EditRateLink component="tile" rateId={rate.id} testid="replace-tile-edit" />}
              </span>
            </span>
            <select value={cfg.rate_id || (options[0]?.id || '')} disabled={busy}
                    data-testid="replace-tile-rate-id"
                    onChange={(e) => onChange({ ...cfg, rate_id: e.target.value })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              {options.length === 0 && <option value="">No rate rows</option>}
              {options.map(o => <option key={o.id} value={o.id}>{o.name} · ${o.rate.toFixed(2)}/ea</option>)}
            </select>
          </label>
          <label className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Qty {inherit && <span className="text-emerald-600 normal-case font-normal">· auto</span>}</span>
            <input type="number" step="1" min="0" value={cfg.qty_override ?? ''} placeholder={defaultQty > 0 ? `auto: ${defaultQty}` : '0'}
                   data-testid="replace-tile-qty-override"
                   onChange={(e) => onChange({ ...cfg, qty_override: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
          </label>
          <div className="col-span-3 inline-flex bg-slate-100 rounded p-0.5 self-end h-7" role="tablist" data-testid="replace-tile-mode">
            {[{ v: 'per_tile', label: '$/tile' }, { v: 'hourly', label: 'Hourly' }].map(o => (
              <button key={o.v} type="button" disabled={busy}
                      data-testid={`replace-tile-mode-${o.v}`}
                      onClick={() => onChange({ ...cfg, mode: o.v })}
                      className={`px-3 py-1 text-xs font-semibold rounded transition-all ${mode === o.v ? 'bg-amber-700 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>{o.label}</button>
            ))}
          </div>
          <div className={`col-span-6 p-2 rounded ${mode === 'per_tile' ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-slate-200'}`}>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] text-slate-500">$ Labour per tile</span>
              <input type="number" step="0.5" min="0" value={cfg.labour_per_tile ?? 0}
                     data-testid="replace-tile-labour-per-tile"
                     onChange={(e) => onChange({ ...cfg, labour_per_tile: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                     className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
            </label>
            <div className="text-[11px] mt-1.5">{qty} tiles × ${lpt.toFixed(2)} = <strong>${perTileLab.toFixed(2)}</strong></div>
          </div>
          <div className={`col-span-6 p-2 rounded ${mode === 'hourly' ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-slate-200'}`}>
            <div className="grid grid-cols-2 gap-1">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-slate-500">Min/tile</span>
                <input type="number" step="0.5" min="0" value={cfg.minutes_per_tile ?? 8}
                       data-testid="replace-tile-minutes-per-tile"
                       onChange={(e) => onChange({ ...cfg, minutes_per_tile: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                       className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-slate-500">$ / hr</span>
                <input type="number" step="5" min="0" value={cfg.hourly_rate ?? 75}
                       data-testid="replace-tile-hourly-rate"
                       onChange={(e) => onChange({ ...cfg, hourly_rate: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                       className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
              </label>
            </div>
            <div className="text-[11px] mt-1.5">{qty} × {mins}min = <strong>{hours.toFixed(2)} hr</strong> × ${hourly} = <strong>${hourlyLab.toFixed(2)}</strong></div>
          </div>
          <div className="col-span-12 text-xs text-slate-600">
            <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Supply: {qty} × ${rate ? effRate.toFixed(2) : '0.00'} = <strong>${supplyTotal.toFixed(2)}</strong> + Labour ${labTotal.toFixed(2)} = <strong className="text-amber-800">${total.toFixed(2)}</strong>{hasOverride && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 font-bold">· override</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Rust Treatment — locations + per-step editor ─────────────────────


function RustTreatmentSection({ cfg, dims, roof_m2, busy, onChange, onEditSteps }) {
  const locM2 = (loc) => {
    if (!loc.enabled) return 0;
    const v = Number(loc.count_value || 0);
    if (loc.count_mode === 'every_nail')      return Number(dims.perimeter_m) * Number(loc.nails_per_m || 4) * Number(loc.m2_per_nail || 0.01);
    if (loc.count_mode === 'percent_m2')      return Number(roof_m2 || 0) * v / 100;
    if (loc.count_mode === 'percent_ridge_m') return Number(dims.ridge_total_m || 0) * (v / 100) * Number(loc.m2_per_ridge_m || 0.05);
    if (loc.count_mode === 'ridge_m_direct')  return v * Number(loc.m2_per_ridge_m || 0.05);
    return v; // m2_direct
  };
  const totalM2 = (cfg.locations || []).reduce((acc, l) => acc + locM2(l), 0)
                + (cfg.other_items || []).reduce((acc, o) => acc + Number(o.m2 || 0), 0);
  const steps = cfg.steps || {};
  const stepTotal = (s) => Number(s?.mat_per_m2 || 0) + Number(s?.lab_per_m2 || 0) + Number(s?.buy_per_m2 || 0) + Number(s?.contr_per_m2 || 0);
  const grand = (cfg.enabled && totalM2 > 0) ? totalM2 * Object.values(steps).reduce((a, s) => a + stepTotal(s), 0) : 0;

  const updateLoc = (idx, patch) => {
    const next = (cfg.locations || []).map((l, j) => j === idx ? { ...l, ...patch } : l);
    onChange({ ...cfg, locations: next });
  };
  const updateOther = (idx, patch) => {
    const next = (cfg.other_items || []).map((o, j) => j === idx ? { ...o, ...patch } : o);
    onChange({ ...cfg, other_items: next });
  };
  const addOther = () => onChange({ ...cfg, other_items: [...(cfg.other_items || []), { id: `other_${Date.now()}`, label: 'Other rust patch', m2: 0 }] });
  const removeOther = (idx) => onChange({ ...cfg, other_items: (cfg.other_items || []).filter((_, j) => j !== idx) });

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="rust-treatment-section">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-rose-50 to-amber-50 flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm font-bold text-slate-800 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} data-testid="rust-enabled"
                 onChange={(e) => onChange({ ...cfg, enabled: e.target.checked })}
                 className="rounded text-rose-600 focus:ring-rose-400" />
          <Flame size={13} className="text-rose-600" /> Rust treatment <span className="font-normal text-[11px] text-slate-500">(Wire-brush · Rust-kill · Rust-prime per m²)</span>
        </label>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onEditSteps} data-testid="rust-edit-steps-btn"
                  className="text-[10px] uppercase tracking-wide font-semibold border border-rose-300 bg-white text-rose-700 px-2 py-1 rounded hover:bg-rose-50 inline-flex items-center gap-1">
            <Edit3 size={11} /> Edit per-step rates
          </button>
          <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total · {totalM2.toFixed(2)} m²</div><div className="text-base font-bold text-rose-800 font-mono-num" data-testid="rust-total">${grand.toFixed(2)}</div></div>
        </div>
      </div>
      {cfg.enabled && (
        <div className="p-4 space-y-2 text-xs">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200 pb-1">
            <div className="col-span-1"></div>
            <div className="col-span-3">Location</div>
            <div className="col-span-3">Count mode</div>
            <div className="col-span-2 text-right">Value</div>
            <div className="col-span-3 text-right">m² this location</div>
          </div>
          {(cfg.locations || []).map((loc, idx) => (
            <RustLocationRow key={loc.id} loc={loc} idx={idx} locM2={locM2(loc)}
                              onChange={(patch) => updateLoc(idx, patch)} />
          ))}
          <div className="border-t border-slate-200 pt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Other / free-form rust patches</span>
              <button type="button" onClick={addOther} data-testid="rust-add-other"
                      className="text-[10px] uppercase tracking-wide font-semibold border border-rose-300 bg-white text-rose-700 px-2 py-1 rounded hover:bg-rose-50">+ Add row</button>
            </div>
            {(cfg.other_items || []).map((o, idx) => (
              <div key={o.id} className="grid grid-cols-12 gap-2 items-center py-1" data-testid={`rust-other-row-${idx}`}>
                <input type="text" value={o.label || ''} placeholder="Description"
                       onChange={(e) => updateOther(idx, { label: e.target.value })}
                       data-testid={`rust-other-label-${idx}`}
                       className="col-span-7 px-2 py-1 text-xs border border-slate-300 rounded" />
                <input type="number" step="0.1" min="0" value={o.m2 ?? 0}
                       onChange={(e) => updateOther(idx, { m2: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                       data-testid={`rust-other-m2-${idx}`}
                       className="col-span-3 px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
                <span className="col-span-1 text-[10px] text-slate-500">m²</span>
                <button type="button" onClick={() => removeOther(idx)}
                        data-testid={`rust-other-del-${idx}`}
                        className="col-span-1 text-rose-600 hover:text-rose-800 text-xs">✕</button>
              </div>
            ))}
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mt-2 text-[11px]">
            <strong>{totalM2.toFixed(2)} m²</strong> total · per m² rate sum: ${Object.values(steps).reduce((a, s) => a + stepTotal(s), 0).toFixed(2)} →
            <strong className="text-rose-800 ml-1">${grand.toFixed(2)}</strong>
            <div className="text-slate-500 mt-0.5">Steps: {Object.entries(steps).map(([k, s]) => `${s.label || k} $${stepTotal(s).toFixed(2)}/m²`).join(' · ')}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function RustLocationRow({ loc, idx, locM2, onChange }) {
  const isNail = loc.id === 'nail_holes';
  const isSheet = loc.id === 'sheet_ends';
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-1" data-testid={`rust-loc-${loc.id}`}>
      <input type="checkbox" checked={!!loc.enabled}
             onChange={(e) => onChange({ enabled: e.target.checked })}
             data-testid={`rust-loc-${loc.id}-enabled`}
             className="col-span-1 rounded text-rose-600 focus:ring-rose-400" />
      <div className="col-span-3 text-slate-800 font-medium">{loc.label}</div>
      <select value={loc.count_mode || 'm2_direct'} disabled={!loc.enabled}
              onChange={(e) => onChange({ count_mode: e.target.value })}
              data-testid={`rust-loc-${loc.id}-mode`}
              className="col-span-3 px-2 py-1 text-xs border border-slate-300 rounded bg-white">
        <option value="m2_direct">Direct m²</option>
        {isNail && <option value="every_nail">Every nail</option>}
        {isNail && <option value="percent_m2">% of roof m²</option>}
        {isSheet && <option value="percent_ridge_m">% of ridge length</option>}
        {isSheet && <option value="ridge_m_direct">Ridge m (direct)</option>}
        {!isNail && !isSheet && <option value="percent_m2">% of roof m²</option>}
      </select>
      <input type="number" step="0.1" min="0" value={loc.count_value ?? 0}
             onChange={(e) => onChange({ count_value: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
             disabled={!loc.enabled}
             data-testid={`rust-loc-${loc.id}-value`}
             className="col-span-2 px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
      <div className="col-span-3 text-right font-mono-num text-slate-700">{locM2.toFixed(2)} m²</div>
    </div>
  );
}


// ── Rust per-step editor dialog ───────────────────────────────────────


function RustStepsDialog({ open, onOpenChange, steps, onSave }) {
  const [draft, setDraft] = useState(steps || {});
  useEffect(() => { setDraft(steps || {}); }, [steps, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="rust-steps-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator size={15} /> Rust treatment per-step rates</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 mb-2">Each rust m² incurs all three steps. Compare the $/m² model with the implied hourly cost (m²/hr × hourly_rate) to sanity-check pricing.</div>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {Object.entries(draft).map(([sid, s]) => (
            <StepRow key={sid} sid={sid} step={s} onChange={(patch) => setDraft(d => ({ ...d, [sid]: { ...d[sid], ...patch } }))} />
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rust-steps-cancel">Cancel</Button>
          <Button onClick={() => onSave(draft)} data-testid="rust-steps-save" className="bg-rose-600 hover:bg-rose-700 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepRow({ sid, step, onChange }) {
  const m2Rate = Number(step.mat_per_m2 || 0) + Number(step.lab_per_m2 || 0) + Number(step.buy_per_m2 || 0) + Number(step.contr_per_m2 || 0);
  const m2PerHr = Number(step.m2_per_hour || 25);
  const hourly = Number(step.hourly_rate || 75);
  const impliedHourlyCost = m2Rate * m2PerHr;
  const delta = impliedHourlyCost - hourly;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3" data-testid={`rust-step-${sid}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-slate-800 text-sm">{step.label || sid}</div>
        <div className="text-xs text-slate-600">m² rate sum: <strong className="font-mono-num">${m2Rate.toFixed(2)}/m²</strong></div>
      </div>
      <div className="grid grid-cols-12 gap-2 text-xs">
        {[
          { k: 'buy_per_m2', label: 'Buy $/m²' },
          { k: 'mat_per_m2', label: 'Material $/m²' },
          { k: 'lab_per_m2', label: 'Labour $/m²' },
          { k: 'contr_per_m2', label: 'Contractor $/m²' },
        ].map(({ k, label }) => (
          <label key={k} className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
            <input type="number" step="0.1" min="0" value={step[k] ?? 0}
                   data-testid={`rust-step-${sid}-${k}`}
                   onChange={(e) => onChange({ [k]: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                   className="px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
          </label>
        ))}
        <label className="col-span-3 flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">m² / hr</span>
          <input type="number" step="1" min="0" value={step.m2_per_hour ?? 25}
                 data-testid={`rust-step-${sid}-m2-per-hour`}
                 onChange={(e) => onChange({ m2_per_hour: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                 className="px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
        </label>
        <label className="col-span-3 flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">$ / hr (target)</span>
          <input type="number" step="5" min="0" value={step.hourly_rate ?? 75}
                 data-testid={`rust-step-${sid}-hourly-rate`}
                 onChange={(e) => onChange({ hourly_rate: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                 className="px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
        </label>
        <div className="col-span-6 text-[11px] text-slate-600 self-end">
          <div className={`rounded px-2 py-1 ${Math.abs(delta) < 5 ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : delta > 0 ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
            Implied hourly: <strong>${impliedHourlyCost.toFixed(2)}/hr</strong> ({m2PerHr} m²/hr × ${m2Rate.toFixed(2)})
            {Math.abs(delta) >= 5 && <span> · delta {delta > 0 ? '+' : ''}${delta.toFixed(2)} vs target ${hourly}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Flashings ────────────────────────────────────────────────────────


function FlashingSection({ items = [], api, appraisalId }) {
  // Flashings + Dektites live in the shared Components list (one list, counted once).
  return (
    <details className="bg-white border border-slate-200 rounded-md" open data-testid="flashing-section">
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
        Flashing Components <span className="text-xs font-normal text-slate-500">({items.length} diagram pin{items.length === 1 ? '' : 's'} · priced in the list below)</span>
      </summary>
      <div className="px-4 pb-4 pt-2">
        {appraisalId && <ComponentsFlashingEmbed api={api} appraisalId={appraisalId} pinCount={items.length} />}
      </div>
    </details>
  );
}


// ── Calculated lines panel ──────────────────────────────────────────


function CalculatedLinesPanel({ preview }) {
  const [filter, setFilter] = useState('all');
  const lines = preview.lines || [];
  const stageTotals = preview.stage_totals || { stage_1: 0, stage_2: 0 };
  const stageCounts = preview.stage_counts || { stage_1: lines.filter(l => (l.stage || 1) === 1).length, stage_2: lines.filter(l => (l.stage || 1) === 2).length };
  const visible = lines.filter(l => filter === 'all' ? true : filter === 'stage1' ? (l.stage || 1) === 1 : (l.stage || 1) === 2);
  const visibleTotal = visible.reduce((s, l) => s + (Number(l.total) || 0), 0);
  return (
    <details className="bg-white border border-slate-200 rounded-md" open data-testid="tin-tile-calculated-lines-panel">
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2"><Wrench size={13} /> Calculated lines <span className="text-xs font-normal text-slate-500">({lines.length})</span></summary>
      <div className="px-4 pb-3 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1.5">
          <div className="inline-flex bg-slate-100 rounded p-0.5" role="tablist">
            {[
              { v: 'all', label: `All (${lines.length})`, cls: 'bg-slate-700' },
              { v: 'stage1', label: `Stage 1 — Repairs (${stageCounts.stage_1})`, cls: 'bg-amber-600' },
              { v: 'stage2', label: `Stage 2 — Paint (${stageCounts.stage_2})`, cls: 'bg-emerald-600' },
            ].map(opt => (
              <button key={opt.v} type="button" onClick={() => setFilter(opt.v)} data-testid={`tin-tile-calc-lines-filter-${opt.v}`}
                      className={`px-3 py-1 text-xs font-semibold rounded transition-all ${filter === opt.v ? `${opt.cls} text-white shadow-sm` : 'text-slate-600 hover:bg-white'}`}>{opt.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div><span className="text-slate-500">Stage 1</span> <span className="font-mono-num font-semibold text-amber-800">${stageTotals.stage_1.toFixed(2)}</span></div>
            <div><span className="text-slate-500">Stage 2</span> <span className="font-mono-num font-semibold text-emerald-800">${stageTotals.stage_2.toFixed(2)}</span></div>
            <div><span className="text-slate-500">Total</span> <span className="font-mono-num font-bold text-slate-900">${preview.total.toFixed(2)}</span></div>
          </div>
        </div>
        {visible.length === 0 ? <div className="text-xs text-slate-500 py-2 italic">{lines.length === 0 ? 'No lines yet — enable a section to start.' : 'No lines match this filter.'}</div> : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b border-slate-200">
              <tr><th className="text-left py-1.5 w-16">Stage</th><th className="text-left">Item</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Total</th></tr>
            </thead>
            <tbody>
              {visible.map((l, i) => {
                const s = Number(l.stage || 1);
                const pill = s === 2 ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">Stage 2</span>
                                       : <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">Stage 1</span>;
                return (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5">{pill}</td>
                    <td><div className="font-medium text-slate-800">{l.type_name}</div>{l.notes && <div className="text-[11px] text-slate-400 italic">{l.notes}</div>}</td>
                    <td className="text-right font-mono-num">{l.quantity} {l.unit}</td>
                    <td className="text-right font-mono-num text-slate-600">${l.rate.toFixed(2)}</td>
                    <td className="text-right font-mono-num font-semibold text-slate-800">${l.total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            {filter !== 'all' && (
              <tfoot><tr className="border-t-2 border-slate-300 font-bold"><td colSpan={4} className="py-2 text-right text-slate-700">{filter === 'stage1' ? 'Stage 1 — Repairs sub-total' : 'Stage 2 — Paint sub-total'}</td><td className="py-2 text-right font-mono-num text-slate-900">${visibleTotal.toFixed(2)}</td></tr></tfoot>
            )}
          </table>
        )}
      </div>
    </details>
  );
}
